import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";

export type ToastTone = "neutral" | "go" | "review" | "stop";

export type ToastRecord = {
  id: string;
  tone: ToastTone;
  title?: string;
  message: ReactNode;
  duration: number;
  epoch: number;
};

type ToastInput = {
  tone?: ToastTone;
  title?: string;
  message: ReactNode;
  /** Milliseconds. `0` keeps the toast until dismissed. */
  duration?: number;
};

const DEFAULT_DURATION: Record<ToastTone, number> = {
  neutral: 4500,
  go: 4000,
  review: 5500,
  stop: 0,
};

const MAX_VISIBLE = 3;

type Listener = (items: ToastRecord[]) => void;

let items: ToastRecord[] = [];
const listeners = new Set<Listener>();

function emit() {
  for (const listener of listeners) listener(items);
}

function sameMessage(a: ReactNode, b: ReactNode) {
  return a === b;
}

function showToast(input: ToastInput): string {
  const tone = input.tone ?? "neutral";
  const duration = input.duration ?? DEFAULT_DURATION[tone];
  const existing = items.find(
    (item) => item.tone === tone && sameMessage(item.message, input.message) && item.title === input.title
  );
  if (existing) {
    items = items.map((item) =>
      item.id === existing.id ? { ...item, duration, epoch: item.epoch + 1 } : item
    );
    emit();
    return existing.id;
  }
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `toast-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  items = [
    ...items,
    { id, tone, title: input.title, message: input.message, duration, epoch: 0 },
  ].slice(-MAX_VISIBLE);
  emit();
  return id;
}

export function dismissToast(id: string) {
  const next = items.filter((item) => item.id !== id);
  if (next.length === items.length) return;
  items = next;
  emit();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  listener(items);
  return () => {
    listeners.delete(listener);
  };
}

function toastFn(message: ReactNode, extras?: Omit<ToastInput, "message">) {
  return showToast({ ...extras, message });
}

toastFn.go = (message: ReactNode, extras?: Omit<ToastInput, "message" | "tone">) =>
  showToast({ ...extras, tone: "go", message });
toastFn.stop = (message: ReactNode, extras?: Omit<ToastInput, "message" | "tone">) =>
  showToast({ ...extras, tone: "stop", message });
toastFn.review = (message: ReactNode, extras?: Omit<ToastInput, "message" | "tone">) =>
  showToast({ ...extras, tone: "review", message });
toastFn.dismiss = dismissToast;

/** Imperative feedback: `toast.go("Guardado.")` / `toast.stop("No se pudo guardar.")`. */
export const toast = toastFn;

const TONE_ICON: Record<ToastTone, string> = {
  neutral: "bg-slate-100 text-ink-soft",
  go: "bg-go-soft text-go",
  review: "bg-review-soft text-review",
  stop: "bg-stop-soft text-stop",
};

function ToneGlyph({ tone }: { tone: ToastTone }) {
  const className = "h-5 w-5";
  if (tone === "go") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path
          d="M5 13l4 4L19 7"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (tone === "review") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path
          d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (tone === "stop") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="2" />
        <path d="M12 8v5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M12 16.5h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="2" />
      <path d="M12 11v6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M12 8h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function ToastBubble({
  item,
  onDismiss,
}: {
  item: ToastRecord;
  onDismiss: (id: string) => void;
}) {
  const paused = useRef(false);
  const remaining = useRef(item.duration);
  const startedAt = useRef(0);
  const timer = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current != null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const arm = useCallback(() => {
    clearTimer();
    if (remaining.current <= 0) return;
    startedAt.current = Date.now();
    timer.current = window.setTimeout(() => onDismiss(item.id), remaining.current);
  }, [clearTimer, item.id, onDismiss]);

  useEffect(() => {
    remaining.current = item.duration;
    if (!paused.current) arm();
    return clearTimer;
  }, [arm, clearTimer, item.duration, item.epoch, item.id]);

  function pause() {
    if (paused.current || remaining.current <= 0) return;
    paused.current = true;
    remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    clearTimer();
  }

  function resume() {
    if (!paused.current) return;
    paused.current = false;
    arm();
  }

  const role = item.tone === "stop" ? "alert" : "status";

  return (
    <div
      role={role}
      className="pointer-events-auto flex w-full items-start gap-3 rounded-2xl bg-white p-3.5 shadow-toast ring-1 ring-slate-900/10 animate-toast-in motion-reduce:animate-none motion-reduce:opacity-100"
      onPointerEnter={pause}
      onPointerLeave={resume}
      onFocusCapture={pause}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) resume();
      }}
      onTouchStart={(e) => {
        touchStartY.current = e.touches[0]?.clientY ?? null;
      }}
      onTouchEnd={() => {
        touchStartY.current = null;
      }}
      onTouchMove={(e) => {
        if (touchStartY.current == null) return;
        const dy = (e.touches[0]?.clientY ?? 0) - touchStartY.current;
        if (dy > 48) {
          touchStartY.current = null;
          onDismiss(item.id);
        }
      }}
    >
      <span
        className={cn(
          "mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full",
          TONE_ICON[item.tone]
        )}
      >
        <ToneGlyph tone={item.tone} />
      </span>
      <div className="min-w-0 flex-1 pt-1">
        {item.title ? <p className="font-semibold text-ink">{item.title}</p> : null}
        <div className={cn("break-words text-sm leading-snug text-ink", item.title && "mt-0.5")}>
          {item.message}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-ink-muted hover:bg-slate-100 hover:text-ink md:h-8 md:w-8"
        aria-label="Cerrar aviso"
      >
        <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
          <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

const KEYBOARD_MIN_PX = 120;

function isKeyboardField(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/** Lift toasts only for an open software keyboard — not URL-bar or rubber-band jitter. */
function useOccludedBottom() {
  const [occluded, setOccluded] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () => {
      if (!isKeyboardField(document.activeElement)) {
        setOccluded(0);
        return;
      }
      if (!viewport) {
        setOccluded(0);
        return;
      }
      const hidden = window.innerHeight - viewport.height - viewport.offsetTop;
      setOccluded(hidden >= KEYBOARD_MIN_PX ? Math.round(hidden) : 0);
    };
    update();
    window.addEventListener("resize", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    viewport?.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
      viewport?.removeEventListener("resize", update);
    };
  }, []);

  return occluded;
}

/** Fixed viewport: full-width snackbar on phones, bottom-left bubble on md+. */
export function ToastHost() {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const [mounted, setMounted] = useState(false);
  const occludedBottom = useOccludedBottom();

  useEffect(() => {
    setMounted(true);
    return subscribe(setToasts);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col gap-2 pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))] pb-[max(0.75rem,calc(env(safe-area-inset-bottom,0px)+var(--app-bottom-nav,0px)))] pt-2 md:inset-x-auto md:bottom-6 md:left-[max(1.5rem,env(safe-area-inset-left,0px))] md:w-[min(22.5rem,calc(100vw-2rem))] md:px-0 md:pb-0"
      style={
        occludedBottom
          ? { marginBottom: occludedBottom, paddingBottom: "0.75rem" }
          : undefined
      }
      aria-live="polite"
      aria-relevant="additions text"
    >
      {toasts.map((item) => (
        <ToastBubble key={item.id} item={item} onDismiss={dismissToast} />
      ))}
    </div>,
    document.body
  );
}

/**
 * Mirrors a page-level message into the toast viewport. Renders nothing in-flow
 * so feedback stays visible while the user is scrolled away from the top.
 */
export function Flash({
  tone = "neutral",
  title,
  children,
}: {
  tone?: ToastTone;
  title?: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (children == null || children === false || children === "") return;
    showToast({ tone, title, message: children });
  }, [tone, title, children]);
  return null;
}
